/**
 * #10 訂單完成頁 — LIFF /orders/:id
 *
 * Phase 1 mock 金流:訂單已寫入 Firestore 但未付款。
 * 顯示訂單摘要 + 收件資訊 + 「金流接通後將通知」提示 + 回首頁 / 看商品按鈕。
 */
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Check, Clock } from "lucide-react";
import StatusMark from "../components/StatusMark";
import { getOrder, type Order } from "../api/cart";
import { jkopayCheckout, jkopayErrorText } from "../api/payments";
import { liffSdk } from "../liff";
import { useUIStore } from "../store/uiStore";


export default function OrderComplete() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [paying, setPaying] = useState(false);

  useEffect(() => {
    if (!id) {
      setErr("missing_order_id");
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const o = await getOrder(id);
        setOrder(o);
      } catch (e: any) {
        setErr(e?.response?.status === 404 ? "not_found" : "load_failed");
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  // 待付款(街口)單 — 每 5 秒刷新,付款 callback 入帳後畫面自動轉「已成立」
  const isPendingPay = order?.status === "pending_payment";
  useEffect(() => {
    if (!id || !isPendingPay) return;
    const t = setInterval(async () => {
      try {
        const o = await getOrder(id);
        setOrder(o);
      } catch { /* 下一輪再試 */ }
    }, 5000);
    return () => clearInterval(t);
  }, [id, isPendingPay]);

  // 續付 — 重取付款網址(街口同單冪等)+ 外開(LINE webview 不能自動轉導,要使用者點擊)
  async function payNow() {
    if (!id || paying) return;
    setPaying(true);
    try {
      const r = await jkopayCheckout(id);
      if (r.status === "ok" && r.payment_url) {
        try {
          liffSdk().openWindow({ url: r.payment_url, external: true });
        } catch {
          window.open(r.payment_url, "_blank");
        }
      } else if (r.status === "already_paid") {
        const o = await getOrder(id);
        setOrder(o);
      } else {
        // 以前這裡什麼都不做 —— 使用者按下付款、畫面毫無反應,連錯在哪都不知道
        useUIStore.getState().showToast(jkopayErrorText(r), "error");
      }
    } catch {
      useUIStore.getState().showToast("連線異常,請稍後再試", "error");
    } finally {
      setPaying(false);
    }
  }

  if (loading) {
    return <div className="min-h-screen bg-cream flex items-center justify-center text-muted">載入中…</div>;
  }

  if (err || !order) {
    return (
      <div className="min-h-screen bg-cream flex flex-col items-center justify-center gap-4 px-5 text-center">
        <div className="text-muted">{err === "not_found" ? "找不到此訂單" : "訂單載入失敗"}</div>
        <button onClick={() => navigate("/home")} className="btn-primary">返回首頁</button>
      </div>
    );
  }

  const addr = order.address || {};
  const addrLine1 = [addr.city, addr.district].filter(Boolean).join(" ");
  const addrLine2 = addr.detail || "";

  return (
    <div className="min-h-screen bg-cream pb-12">
      <header className="bg-primary-dark text-white px-5 py-8 text-center">
        <StatusMark icon={isPendingPay ? Clock : Check} tone="onDark" className="mb-3" />
        <h1 className="text-2xl font-bold">{isPendingPay ? "訂單待付款" : "訂單已成立"}</h1>
        <div className="text-sm opacity-80 mt-2">訂單編號 #{order.order_id}</div>
      </header>

      <div className="px-5 mt-5 space-y-4">
        {isPendingPay ? (
          <div className="card bg-sage-light/20 border border-sage-light space-y-3">
            <div className="text-sm text-ink">
              此訂單尚未完成付款。付款完成後才會成立出貨,系統將自動更新狀態。
            </div>
            <button onClick={payNow} disabled={paying} className="btn-primary bg-jkopay w-full">
              {paying ? "開啟中…" : `前往街口支付 · NT$ ${order.total.toLocaleString()}`}
            </button>
          </div>
        ) : order.payment_method === "jkopay" ? (
          <div className="card bg-sage-light/20 border border-sage-light text-sm text-ink">
            已收到您的付款(街口支付),我們將盡快為您安排出貨。
          </div>
        ) : (
          <div className="card bg-sage-light/20 border border-sage-light text-sm text-ink">
            本訂單為模擬下單,資料已記錄至會員系統。<br />
            金流接通後將通知您完成付款,屆時請依專屬連結結帳。
          </div>
        )}

        <div className="card space-y-3">
          <div className="text-sm text-muted font-medium">收件資訊</div>
          <div className="text-sm space-y-1">
            <div><span className="text-muted">收件人</span>　{order.name || "—"}</div>
            <div><span className="text-muted">聯絡電話</span>　{order.phone || "—"}</div>
            <div><span className="text-muted">寄送地址</span>　{addrLine1 || "—"}</div>
            {addrLine2 && <div className="pl-[60px]">{addrLine2}</div>}
          </div>
        </div>

        <div className="card space-y-3">
          <div className="text-sm text-muted font-medium">訂單明細</div>
          <div className="space-y-2 text-sm">
            {order.items.map((it) => (
              <div key={it.product_id + it.mode} className="flex justify-between gap-3">
                <div className="flex-1">
                  <div>{it.name}</div>
                  <div className="text-xs text-muted">{it.spec} · NT$ {it.price_each.toLocaleString()} × {it.qty}</div>
                </div>
                <div className="text-ink">NT$ {it.subtotal.toLocaleString()}</div>
              </div>
            ))}
          </div>
          <div className="border-t border-line pt-3 flex justify-between">
            <span className="text-muted">總計</span>
            <span className="text-lg font-bold text-ink">NT$ {order.total.toLocaleString()}</span>
          </div>
        </div>

        {order.note && (
          <div className="card">
            <div className="text-sm text-muted font-medium mb-2">備註</div>
            <div className="text-sm text-ink whitespace-pre-wrap">{order.note}</div>
          </div>
        )}

        <div className="space-y-2 mt-6">
          <button onClick={() => navigate("/products")} className="btn-primary w-full">繼續選購</button>
          <button onClick={() => navigate("/home")} className="btn-secondary w-full">回會員首頁</button>
        </div>
      </div>
    </div>
  );
}
